import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { UserX } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button as CustomButton } from '@/components/Button/CustomButton';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
	children: ReactNode;
	requiredRoles?: string[];
}

export default function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
	const { isAuthenticated, isLoading, user, error, login, logout, refreshUserProfile } = useAuth();
	const { inProgress } = useMsal();

	// When not authenticated, trigger login. MSAL's redirectStartPage (set in login()) restores the
	// current URL after redirect via CustomNavigationClient — no manual sessionStorage needed.
	useEffect(() => {
		if (!isLoading && !isAuthenticated && inProgress === InteractionStatus.None) {
			login();
		}
	}, [isLoading, isAuthenticated, inProgress, login]);

	// Signed in with Azure AD but the profile request failed for good: without this the check below
	// reads the missing roles as "still loading" and spins forever.
	const profileFailed =
		!isLoading && isAuthenticated && inProgress === InteractionStatus.None && error !== null && !user?.roles;

	if (profileFailed) {
		return (
			<Container>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						textAlign: 'center',
						minHeight: '80vh',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 72,
							height: 72,
							borderRadius: '50%',
							background: 'var(--error-100)',
							color: 'var(--error-500)',
							mb: 3,
						}}
					>
						<UserX size={32} />
					</Box>
					<Typography sx={{ fontSize: '20px', fontWeight: 600, color: 'var(--neutral-gray-dark)', mb: 1 }}>
						We couldn't load your account
					</Typography>
					<Typography sx={{ fontSize: '14px', color: 'var(--neutral-gray-medium)', maxWidth: 420, mb: 4 }}>
						{error}
					</Typography>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<CustomButton
							variant="outlined"
							size="large"
							text="Log out"
							onClick={() => logout().catch(() => {})}
						/>
						<CustomButton
							variant="contained"
							size="large"
							text="Try again"
							onClick={() => void refreshUserProfile()}
						/>
					</Box>
				</Box>
			</Container>
		);
	}

	if (
		isLoading ||
		!isAuthenticated ||
		inProgress !== InteractionStatus.None ||
		(user !== null && user.roles === undefined)
	) {
		return (
			<Container>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '80vh',
					}}
				>
					<CircularProgress />
				</Box>
			</Container>
		);
	}

	if (requiredRoles && requiredRoles.length > 0 && user) {
		const hasRequiredRole = requiredRoles.some((role) => user.roles?.some((r) => r.roleName === role));
		if (!hasRequiredRole) {
			const userRoleNames = user.roles?.map((r) => r.roleName) ?? [];
			const fallback = userRoleNames.includes('FieldTech') ? '/work-orders' : '/assets-register';
			return (
				<Navigate
					to={fallback}
					replace
				/>
			);
		}
	}

	return <>{children}</>;
}
